define((require) => {
    "use strict";

    const $                = require("jquery");
    const _                = require("underscore");
    const Promise          = require("shared/util/Promise");
    const {
        openAdapted,
        topWindowAdapted,
        visitAdapted,
        runAdapted
    }                      = require("shared/xp/adapters/WindowAdapter");

    // Window referenced wrapped in a jQuery element...
    const $win = $(window);

    // Global flag to turn on/off all window listeners added by "on", "until", "unload", or "error", set via
    // Win.active(active: boolean). Used to pause all "global" handlers while the Viewer is hidden on
    // for example an adjacent scene. This is really not the best way to do this, but the global state
    // is at the very core dependent of the ExperienceViewModel used in the ExperienceController, which
    // can be delegated from GroupController and what not, with no access to said model. So, instead
    // the ExperienceController will notify Window.active(..) when it changes from active to inactive or
    // vice versa, without intermediate controllers and what not requiring to handle this in numerous
    // places...
    let _active = true; // TODO: this should then be a general concept, no? E.g. also for window.document

    /**
     * @class Win
     * Window abstraction for hooking up events directly on window.
     */
    const Win = { // Explicit different from Browser, since used in both Viewer, Editor, And Channels Admin apps!
        // Naming to ensure we do not conflict native Window type here, might want to use it...

        /**
         * User agent for the current window.
         */
        userAgent: window.navigator.userAgent,

        /**
         * Visits all accessible parent windows in reverse order, optionally invoking the specified callback when
         * each such window is visited.
         *
         * @param {Function} [callback] Optional callback like <tt>fn(parent: Window, window: Window, error:Error): *</tt>
         *                              to be invoked for each accessible window, which will break the traversal if a
         *                              defined value is returned. If a non accessible window is encountered, the
         *                              parent argument will be null and the error will be supplied as the third
         *                              argument. Will always be invoked at least once (perhaps will null parent) if
         *                              supplied.
         *
         * @return {Window|*} The top most accessible window, or the first defined value returned by <tt>callback</tt>.
         */
        visit(callback) {
            return visitAdapted(callback);
        },

        /**
         * Returns a reference to the current window ("self").
         *
         * @return {Window} The current window; never falsey.
         */
        self() {
            return window;
        },

        /**
         * Returns the parent window if present and accessible. Always use this function to get a reference to the
         * patent window, never do "window.parent" as it may not be accessible! <p>
         *
         * @return {Window} The parent window we have access to, if any; can be null if no parent or if the parent
         *                  is not accessible (e.g. on direct url).
         *
         * @see    #topWindow
         */
        parent() {
            return Win.visit((parent) => parent); // will cause "visit" to break immediately
        },

        /**
         * Returns the top-most window currently accessible. Always use this function to get a reference to the
         * top most window, never do "window.top" as it may not be accessible! <p>
         *
         * This will not always be the top level window, for example when running DnD in framed mode in IE, but the
         * same can in principle be true for any client site.
         *
         * @param  {Boolean} [excludeSelf] True to return null if the top most accessible window is this window,
         *                                 false otherwise (default).
         * @return {Window} The top-most level window we have access to; never falsey, will default to this window.
         *
         * @see    #parent
         * @see    #run
         */
        topWindow(excludeSelf) {
            return topWindowAdapted(excludeSelf);
        },

        /**
         * Short-cut method to access the ViewerProxy for a Viewer instance in some specific Window, optionally
         * a ViewerProxy property, e.g. a function, which can also be invoked if found. <p>
         *
         * A call like <tt>Window.viewer("parent", "state")</tt> is equivalent to
         * <tt>Window.sandbox("viewer.state", Window.parent())</tt>; or <tt>Window.viewer("topWindow")</tt> to
         * <tt>Window.sandbox("viewer", Window.topWindow())</tt>; or <tt>Window.viewer()</tt> to
         * <tt>Window.sandbox("viewer", Window.self())</tt>. <p>
         *
         * To call said state method, if present, do something like
         * <tt>Window.viewer(win, "state", {name: "active": value: false})</tt>, which will return the result
         * of the invocation. If no such method to call is found, the call is simply a no-op. <p>
         *
         * Caveat 1: the top most window on a direct url will return the Viewer itself if called from there, while
         * the same call when embedded via channels will return null as the top window will there be the client
         * window doing the embedding via channels. <p>
         *
         * Caveat 2: the Viewer defined in the parent window may not be the logical parent of the Viewer defined
         * in the current window if the current Viewer is opened in a Lightbox. This is because Lightboxes live
         * in the top most window/dom, but logically such Viewers live in the iframe window used by the Lightbox.
         * That is, <tt>Window.viewer("parent")</tt> may <b>not</b> be the same as
         * <tt>Window.viewer("self", "parent")</tt> as the first will access <tt>window.parent.viewer</tt> and
         * the latter <tt>window.viewer.parent</tt>. <p>
         *
         * So - to find the top most reachable viewer in any scenario, from any ancestor window, do
         * <tt>Window.viewer().root()</tt>. <p>
         *
         * @param {String|*} [where]    A string like "self", "parent", or "topWindow" to describe any function
         *                              defined on this type to return a Window instance, or a scope as is; can
         *                              be undefined if no other arguments, resulting in the Viewer will be looked
         *                              up in the current window/iframe.
         * @param {String}   [property] Optional property path, supporting dot notation, to access some recursive
         *                              property in the Viewer (like a sandbox path); if not specified, the ViewerProxy
         *                              itself is merely returned.
         * @param  {*}       [varArgs]  Optional arguments to invoke the specified function via <tt>property</tt>,
         *                              returning its value in place of the ViewerProxy property.
         * @return {*} The ViewerProxy or the specified ViewerProxy property; can be falsey.
         */
        viewer(where = "self", path, ...args) {
            let p = "viewer";

            if (path) {
                p += `.${path}`;
            }
            // viewer() -> "self", but not otherwise...
            if (typeof where === "string") { // e.g. "self"
                where = Win[where]();
            } else if ("contentWindow" in where) { // iframe
                where = where.contentWindow;
            }
            const v = Win.sandbox(p, where); // e.g. a Window

            // We know ViewerProxy functions handle scoping well...
            if (typeof v === "function" && args.length > 0) {
                return v(...args);
            }

            return v;
        },

        /**
         * Computes the offset (top left coordinates) of the specified element, relative to the top most window/document.
         *
         * @param  {HTMLElement|jQuery} element The target element.
         * @return {Object} The offset, e.g. <tt>{top: 100, left: 100}</tt>.
         */
        offset(element) {
            const $element = (element instanceof $) ? element : $(element);
            const offset = $element.offset();

            Win.visit((parent) => {
                if (parent) { // will be null if not accessible...
                    const frameOffset = $(parent.frameElement).offset();
                    if (frameOffset) {
                        offset.top  += frameOffset.top;
                        offset.left += frameOffset.left;
                    }
                }
            });

            return offset;
        },

        /**
         * Runs the specified code in a given window. <p>
         *
         * <tt>options</tt> can be a string, the code to evaluate in the top most accessible window, or it can have
         * the following format for more control:
         * <pre>
         *    {
         *        code:    String,  // Mandatory code to run (eval) in <tt>options.win</tt>.
         *        win:     Window,  // Optional window to run the code in, defaulting to the top most accessible one.
         *        errback: Function // Optional errback handler to suppress errors; if not specified, errors will be thrown as is.
         *    }
         * </pre>
         *
         * @param {String|{code:String, win:Window, errback:Function}} options
         *        If a string, the code to run (eval) in the top most accessible window; otherwise a set of options as
         *        described above; cannot be falsey.
         *
         * @return {*} The result of the evaluation, if any; can be any value.
         *
         * @throws {Error} If the run (eval) raises an error and <tt>errback</tt> is not supplied to handle it.
         */
        run(options) {
            return runAdapted(options);
        },

        /**
         * Returns a reference to the specified sandbox in client space under <tt>scope</tt> based on <tt>path</tt>,
         * where <tt>scope</tt> will default to the top most accessible window. If <tt>scope</tt> is already supplied,
         * it can be any type of object. If a cross domain Window, the sandbox will always be off limit and undefined
         * will be returned. The same is true if <tt>path</tt> at some point will reference a cross domain value. <p>
         *
         * This will <b>not</b> create any such sandbox, merely reference it and its properties (when accessible).
         * If you need to export something (to some global sandbox), this must be done explicitly elsewhere. <p>
         *
         * By convention, all properties/values under this scope are considered read-only.
         *
         * @param  {String|String[]} path    Mandatory path to access some property or the sandbox itself under <tt>scope</tt>;
         *                                   cannot be falsey, and supports dot notation. If an array, each path in it will
         *                                   be looked up and the first one returning a defined value will be used.
         * @param  {Window|*}        [scope] The scope to access the sandbox in, defaulting to the top most accessible window
         *                                   scope on undefined, but not on null (on null, undefined will always be returned).
         *                                   Can be any object type.
         *
         * @return {*} The specified sandbox/property; can be any value.
         */
        sandbox(path, scope) {
            const paths = typeof path === "string" ? [path] : path;
            let parts;
            let o;
            let i;
            let j;

            // "scope" can be null, for example if the scope is acquired via this function by an earlier call, which
            // will result in undefined being returned by this function. We differentiate between null and undefined
            // to allow a sensible default (top window) when no scope is supplied...
            if (typeof scope === "undefined") {
                scope = Win.topWindow(); // will be accessible!
            }

            // eslint-disable-next-line eqeqeq
            if (scope != null) {
                // Look up specific path, e.g. "window.__zmags.lightbox"?
                loop:
                for (i = 0; i < paths.length; i++) {
                    // Reset to original scope for each path to traverse...
                    o = scope;
                    parts = paths[i].split(".");
                    for (j = 0; j < parts.length; j++) {
                        try {
                            o = o[parts[j]];
                        } catch (e) { // e.g. Security Error, TypeError (non-readable property)
                            o = null;
                            // Fall-through
                        }
                        // eslint-disable-next-line eqeqeq
                        if (o == null) {
                            continue loop;
                        }
                    }
                    return o;
                }
            }

            // undefined
        },

        /**
         * Returns a Promise that will resolve once the specified sandbox in client space under <tt>scope</tt> based
         * on <tt>path</tt> is defined and not null, where <tt>scope</tt> will default to the top most accessible
         * window. The resolved value is the sandbox value (never null, nor undefined), but the returned promise
         * may be rejected after <tt>timeout</tt> milliseconds if the sandbox value is not accessible, not defined,
         * and not null by then. <p>
         *
         * If <tt>scope</tt> is already supplied as a cross domain Window, the sandbox will always be off limit and
         * the promise will be rejected after the specified timeout always. The same is true if <tt>path</tt> at some point reference a cross domain value. <p>
         *
         * This will <b>not</b> create any such sandbox, merely reference it and its properties (when accessible).
         * If you need to export something (to some global sandbox), this must be done explicitly elsewhere. <p>
         *
         * By convention, all properties/values under this scope are considered read-only.
         *
         * @param  {String|String[]} path      Mandatory path to access some property or the sandbox itself under <tt>scope</tt>;
         *                                     cannot be falsey, and supports dot notation. If an array, each path in it will
         *                                     be looked up and the first one returning a defined value will be used.
         * @param  {Window|*}        [scope]   The scope to access the sandbox in, defaulting to the top most accessible window
         *                                     scope on undefined, but not on null (on null, undefined will always be returned).
         *                                     Can be any object (non-primitive) type.
         * @param  {Number}          [timeout] The timeout in milliseconds to poll for the sandbox before giving up; will default
         *                                     to 5 seconds. Can be supplied as the second argument as well if the top most
         *                                     accessible window is the scope.
         *
         * @return {Promise} A Promise that will resolve with the specified sandbox/property, which can be any value, or that
         *                   will be rejected after <tt>timeout</tt> ms. The promise may already have been resolved upon
         *                   return, i.e. if the sandbox is already available at call time.
         * @see    #sandbox
         */
        sandboxPromise(path, scope, timeout) {
            if (typeof scope === "number") {
                timeout = scope;
                scope = undefined; // will default to top most window
            }
            const deferred = Promise.deferred();
            const promise = deferred.promise();
            let timer;
            let start;

            const clearTimer = () => {
                if (timer) {
                    window.clearTimeout(timer);
                    timer = null;
                }
            };

            const poll = () => {
                const result = Win.sandbox(path, scope);
                // eslint-disable-next-line eqeqeq
                if (result != null) {
                    clearTimer();
                    deferred.resolve(result);
                    return false;
                }

                return true; // keep polling
            };

            // Do an initial poll before firing up a polling interval...
            if (poll()) {
                timeout = timeout || 5000; // 5s
                start = Date.now();

                timer = window.setInterval(() => {
                    // "poll" will resolve and clear timer on success, we only need to reject on timeout...
                    if (poll() && (Date.now() - start > timeout)) {
                        clearTimer();
                        deferred.reject();
                    }
                }, 200);
            }

            return promise;
        },

        /**
         * Specifies whether or not all "on", "until", "unload", and "error" listeners are active or inactive,
         * default active. This will not remove/add any listeners, merely activate/deactivate listeners
         * already defined. <p>
         *
         * This can be used to temporarily turn off window events while this Viewer does not have
         * focus, e.g. hidden on some adjacent scene current not shown.
         *
         * @param {Boolean} [active] True to activate, falsey to deactivate. If not specified, the current
         *                           active state is returned.
         * @return {Boolean} True if now active, false if inactive.
         *
         * @see    #on
         * @see    #until
         * @see    #unload
         * @see    #error
         */
        active(active) {
            if (arguments.length) {
                _active = active;
            }
            return _active;
        },

        /**
         * Open a new window via the top most reachable window, either in the same window or a new window/tab,
         * ensuring a short delay before opening in the same window so stats and/or pending http requests have
         * a chance of completing if the new window/location will replace the current (client) page. <p>
         *
         * Do <b>not</b> use <tt>window.open(..)</tt> or set <tt>window.location</tt> directly, always use this
         * function! <p>
         *
         * Caveat: opening a window via <tt>setTimeout</tt> is subject to pop-up blockers, but this is the only
         * way we can ensure pending requests have a change of completing when updating the location of the
         * current (top-most) window.
         *
         * @param {String} url      The url to open; cannot be falsey.
         * @param {String} [target] The window target/name; will default to "_blank", i.e. new window.
         * @param {String} [specs]  Optional window specs for new windows only; can be falsey.
         *
         * @return {Window} A reference to the JS Window type opened, or null if the opening failed. It will expose
         *                  a <tt>close()</tt> function as per spec.
         */
        open(url, target, specs) {
            return openAdapted(url, target, specs);
        },

        /**
         * Subscribes the supplied callback to the given event(s) for the specified window, either based on a
         * set of options or via event name and associated callback (and window). <p>
         *
         * The options can take the following form:
         * <pre>
         *   {
         *      event:     String,             // space-separated list of event names to listen for on the window in question.
         *      callback:  Function,           // the function to invoke on the specified events.
         *      scope:     *,                  // optional scope for <tt>callback</tt>, defaulting to the function itself.
         *      win:       Window,             // optional window to listen to, defaulting to the current window.
         *      predicate: Function():Boolean, // optional predicate to suppress event callbacks when the predicate
         *                                     // returns false, e.g. to temporarily disable <tt>callback</tt> without
         *                                     // removing it. Will only be called while <tt>Window.active()</tt>
         *                                     // returns true!
         *      throttle:  Number,             // optional throttle delay in ms to wrap <tt>callback</tt> in. Can be combined
         *                                     // with <tt>debounce</tt>.
         *      debounce:  Number,             // optional debounce delay in ms to wrap <tt>callback</tt> in. Can be combined
         *                                     // with <tt>throttle</tt>.
         *   }
         * </pre>
         *
         * Alternatively, this is also legal:
         * <pre>
         *      Window.on("event1 event2 ..", callback, win); // "win" optional, default current window
         * </pre>
         *
         * @param  {String|*} options    If a single argument, the options to use; otherwise the event name(s) as a
         *                               space separated string, requiring <tt>callback</tt> and optionally <tt>win</tt>
         *                               to be specified as well.
         * @param  {Function} [callback] The callback function to be invoked continuously for the specified event(s)
         *                               if <tt>options</tt> is a string; cannot be null if so.
         * @param  {Window}   [win]      The window in question of <tt>options</tt> is a string, defaulting to the
         *                               current one; can be falsey.
         *
         * @return {{off:Function, win:jQuery, callback:Function}} An object storing the actual <tt>callback</tt>
         *         and an <tt>off()</tt> function to remove the callback again; never falsey. For convenience, the
         *         window used is stored under <tt>win</tt> in a jQuery wrapper.
         */
        on(options, ...args) { // event, callback, win) {
            if (typeof options === "string") {
                options = {event: options, callback: args[0], win: args[1]}; // e.g. Window.on("foo", fn, win) allowed
            }
            const $w        = options.win ? $(options.win) : $win;
            const fn        = options.callback;
            const scope     = options.scope || fn;
            const predicate = options.predicate;

            let event    = options.event; // can be more than one
            let listener = null;

            // Predicate is used to temporarily turn of an event, e.g. when this Viewer is hidden on some
            // adjacent scene, but can never override make an inactive Viewer become active...
            let callback = function(...cbArgs) {
                if ($win[0].closed || $w[0].closed) { // remember to off leaked listeners!
                    listener.off();
                } else if (_active && event && (!predicate || predicate())) {
                    fn.apply(scope, cbArgs);
                }
            };

            if (typeof options.throttle === "number") {
                callback = _.throttle(callback, options.throttle);
            }
            if (typeof options.debounce === "number") {
                callback = _.debounce(callback, options.debounce);
            }

            $w.on(event, callback);

            listener = {
                off() {
                    if (event) {
                        $w.off(event, callback);
                        event = null; // ensure off for good, also "callback" reference below
                    }
                },
                win: $w,
                callback,
                toString() {
                    return event ? "off" : event;
                }
            };

            return listener;
        },

        /**
         * Subscribes the supplied callback for the specified event(s) for the specified window, firing on events
         * until <tt>callback</tt> returns a defined truthy value. This means that unlike jQuery.one, the callback
         * will be notified until it says it is done by returning a defined truthy value. <p>
         *
         * It can either be based on a set of options or via event name and associated callback (and window). <p>
         *
         * The options can take the following form:
         * <pre>
         *   {
         *      event:    String,             // space-separated list of event names to listen for on the window in question.
         *      callback: Function: Boolean,  // the function to invoke on the specified events, which will be removed
         *                                    // again once a defined value is returned.
         *      scope:    *,                  // optional scope for <tt>callback</tt>, defaulting to the function itself.
         *      win:      Window,             // optional window to listen to, defaulting to the current window.
         *      throttle: Number,             // optional throttle delay in ms to wrap <tt>callback</tt> in. Can be combined
         *                                    // with <tt>debounce</tt>.
         *      debounce: Number,             // optional debounce delay in ms to wrap <tt>callback</tt> in. Can be combined
         *                                    // with <tt>throttle</tt>.
         *   }
         * </pre>
         *
         * Alternatively, this is also legal:
         * <pre>
         *      Window.until("event1 event2 ..", callback, win); // "win" optional, default current window
         * </pre>
         *
         * @param  {String|*} options    If a single argument, the options to use; otherwise the event name(s) as a
         *                               space separated string, requiring <tt>callback</tt> and optionally <tt>win</tt>
         *                               to be specified as well.
         * @param  {Function} [callback] The callback function to be invoked on the specified event(s) until specified
         *                               if <tt>options</tt> is a string; cannot be null if so. Once this callback returns
         *                               a defined truthy value, it will be removed automatically (if not already
         *                               removed by calling <tt>off()</tt> on the return value.
         * @param  {Window}   [win]      The window in question of <tt>options</tt> is a string, defaulting to the
         *                               current one; can be falsey.
         *
         *
         * @return {{off:Function, callback:Function}} An object storing <tt>callback</tt> and an <tt>off()</tt>
         *         function to remove the callback again.
         */
        until(options, ...args) {
            if (typeof options === "string") {
                options = {event: options, callback: args[0], win: args[1]}; // e.g. Window.on("foo", fn, win) allowed
            }
            let listener;
            const callback = options.callback;

            options.callback = function(...cbArgs) {
                // "callback" will already have been bound to proper scope if specified, so we simply default to the
                // callback it self...
                if (callback.apply(options.scope || callback, cbArgs)) {
                    listener.off();
                }
            };
            listener = Win.on(options);

            return listener;
        },

        /**
         * Subscribes the supplied callback to the <tt>window.onerror</tt> for the specified window.
         *
         * <tt>callback</tt> with be notified with two argument: the first, "resolved", contains a resolved
         * error and message, like <tt>{error:Error, message:String}</tt>, while the second, "event", contains
         * the original jQuery event. To check for the specific error, use "resolved"!
         *
         * @param {Function} callback The listener/callback function to be invoked when a
         *                            runtime error occurs; cannot be null.
         * @param {Window}   [win]    The window in question, defaulting to the current one; can be falsey.
         *
         * @return {{off:Function, callback:Function}} An object storing <tt>callback</tt> and an <tt>off()</tt>
         *         function to remove the callback again.
         */
        error(callback, win) {
            return Win.on("error", (event) => {
                // The originalEvent does not seem to set on mobile devices either, so we have to check for that
                // as well, and create if need be. We have to deduce error and message since jQuery seems to ignore
                // error messages for legacy IE browsers, supplied as arguments to the onerror event handler or set
                // on window.event.errorMessage...
                const original = event.originalEvent || {};
                const message  = original.message || (window.event && window.event.errorMessage) || "Unknown error";
                const error    = original.error || new Error(message); // "error" will be set in legacy IE
                const resolved = {error, message};

                // jQuery event, so we can augment it...
                event.resolved = resolved;

                // Call!...
                return callback(resolved, event);
            }, win);
        },

        /**
         * Subscribes the supplied callback to be notified before unload of the specified window.
         *
         * @param {Function} callback The callback function to be invoked before unload; ignored if not
         *                            a function.
         * @param {Window}   [win]    The window in question, defaulting to the current one; can be falsey.
         *
         * @return {{off:Function, callback:Function}} An object storing <tt>callback</tt> and an <tt>off()</tt>
         *         function to remove the callback again.
         */
        unload(callback, win) {
            return Win.until("beforeunload", callback, win);
        }
    };

    // Add handler to log unhandled errors; we have do to it like this, or we will log the unhandled error for
    // each active error handler.
    //
    // NOTE: this will (may) also log third party errors by the viewer, which may look like the error originated
    //       at the viewer - since catched/logged here - which is in fact not the case for such third party
    //       unhandled errors...
    Win.error((resolved) => { // TODO: should we only log in the editor instead, and not in the viewer?
        // Akin to unhandled exceptions being logged as errors by Events.js. Note that
        // console.error may not be a function!
        /* eslint-disable no-console */
        if (typeof console !== "undefined" && typeof console.error !== "undefined") {
            // No prefix, should mimic unhandled errors when logged by the browser by default...
            console.error(`Uncaught ${(resolved.error.stack || resolved.error)}`);
            // So other listeners can see if this message has been logged...
            resolved.logged = true;
        }
        /* eslint-enable no-console */
    });

    return Win;
});



// WEBPACK FOOTER //
// ./src/shared/services/Window.js